import { CheckboxField, Form } from "@wayflyer/flyui";
import { ConnectionSchemas } from "api-types";
import { axiosClient as client } from "auth-sdk";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { useCustomerAppConfig } from "undercarriage";
import { AccountsForm } from "../../components";
import { accountTransformer } from "../../data-sources";

import { useConnectionFlowsConfig } from "../config";
import { MESSAGES } from "../messages";

export const PropertySelector = ({
  connectorSlug,
  completeConnectionProps,
}: {
  connectorSlug: string;
  completeConnectionProps: ConnectionSchemas.CompleteConnectionPropsSchema;
}) => {
  const intl = useIntl();
  const [, setSearchParams] = useSearchParams();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { completeConnectionSubmitApiUrl, cancelConnectionSubmitApiUrl } = useConnectionFlowsConfig();
  const submitPaths = {
    completeConnectionPath: completeConnectionSubmitApiUrl.replace(":customerSlug", customerDomain),
    cancelConnectionPath: cancelConnectionSubmitApiUrl.replace(":customerSlug", customerDomain),
  };
  const [loading, setLoading] = useState(false);
  const transformedProps = completeConnectionProps
    ? accountTransformer(
        intl,
        completeConnectionProps,
        {},
        {
          //required but never used
          error: () => {
            return;
          },
        },
        submitPaths
      )
    : null;

  const [selectedAccounts, setSelectedAccounts] = useState(transformedProps?.accounts || []);
  const propertySelectionNotRequired =
    transformedProps?.accounts &&
    transformedProps?.accounts.length === 1 &&
    !Array.isArray(transformedProps?.accounts[0].properties);

  const [submitDisabled, setSubmitDisabled] = useState(
    !(selectedAccounts.length === 1 && selectedAccounts[0].selected)
  );

  const handleConfirm = async () => {
    try {
      if (transformedProps) {
        setLoading(true);
        await transformedProps?.connectClickHandler(selectedAccounts);
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelConnection = () => {
    client
      .post(submitPaths.cancelConnectionPath, completeConnectionProps)
      .then(() => setSearchParams({ platform: connectorSlug }));
  };

  return propertySelectionNotRequired ? (
    <Form
      onSubmit={handleConfirm}
      primaryAction={{
        content: intl.formatMessage(MESSAGES.connectLabel),
        disabled: submitDisabled,
        loading: loading,
      }}
      tertiaryAction={{
        content: intl.formatMessage(MESSAGES.cancelLabel),
        onAction: cancelConnection,
      }}
    >
      <CheckboxField
        name="account-property-1"
        options={{
          name: "account-property-1",
          label: (selectedAccounts.length === 1 && selectedAccounts[0].label) as string,
          defaultChecked: selectedAccounts.length === 1 && selectedAccounts[0].selected,
          onChange: () => {
            if (selectedAccounts.length === 1) {
              setSelectedAccounts([{ ...selectedAccounts[0], selected: !selectedAccounts[0].selected }]);
              setSubmitDisabled(selectedAccounts[0].selected);
            }
          },
        }}
      />
    </Form>
  ) : (
    <Form
      onSubmit={handleConfirm}
      primaryAction={{
        content: intl.formatMessage(MESSAGES.connectLabel),
        disabled: transformedProps?.confirmDisabled(selectedAccounts),
        loading: loading,
      }}
      tertiaryAction={{
        content: intl.formatMessage(MESSAGES.cancelLabel),
        onAction: cancelConnection,
      }}
    >
      <AccountsForm
        accounts={selectedAccounts}
        setAccounts={setSelectedAccounts}
        selectAllLabel={intl.formatMessage({
          id: "funding-application: default select all accounts",
          defaultMessage: "Select all",
        })}
      />
    </Form>
  );
};
